import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { AsyncSelect } from '@the-platform-group/component-library/Select'

import { getAgenticPrompts } from 'services/agenticPrompts'
import { AGENTIC_PROMPT_SCOPES } from 'constants/agenticPrompts'
import debounce from 'utils/debounce'

const loadParentPrompts = debounce(async term => {
  const { data } = await getAgenticPrompts({
    scope: AGENTIC_PROMPT_SCOPES.PARENTS,
    term: term || null,
  })
  return data.map(prompt => ({ id: prompt.id, name: prompt.title }))
}, 300)

/**
 * Async typeahead for selecting a parent prompt. Loads non-child prompts
 * from the agentic prompts API and excludes the current prompt (a prompt
 * cannot be its own parent).
 * @param {object} props - component props
 * @param {?{id: string, name: string}} props.value - currently selected parent prompt
 * @param {function} props.onChange - called with the selected option object (or null when cleared)
 * @param {string} [props.excludeId] - prompt id to exclude from results (e.g. the prompt being edited)
 * @param {string} [props.helperText] - optional helper text rendered under the select
 * @param {boolean} [props.error] - whether to render the field in an error state
 * @returns {React.ReactElement} The parent prompt select control
 */
const ParentPromptSelect = ({ value, onChange, excludeId, helperText, error }) => {
  const optionsLoader = useMemo(
    () => async term => {
      const options = await loadParentPrompts(term)
      return excludeId ? options.filter(option => option.id !== excludeId) : options
    },
    [excludeId],
  )

  return (
    <AsyncSelect
      fullWidth
      hideArrow
      includeClearAll
      label="Parent prompt"
      placeholder="Search parent prompts"
      value={value || null}
      onChange={selected => onChange(selected || null)}
      optionsLoader={optionsLoader}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      getOptionLabel={({ option }) => option?.name ?? ''}
      helperText={helperText}
      {...(error && { error: true })}
    />
  )
}

ParentPromptSelect.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  excludeId: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.bool,
}

export default ParentPromptSelect
